import React, { useContext, useEffect, useState } from "react"
import styled from "styled-components"
import Button from "@src/components/core-button"
import UserActions from "@src/components/content-user-flow-modal"
import { transformPricingTable } from "@src/components/self-book/create-quote/utils/transformPricingTable"
import { splitInclusions } from "@src/components/self-book/create-quote/utils/splitInclusions"
import { UserContext } from "@src/context/user-context"
import { useFormContext } from "react-hook-form"
import { format } from "date-fns"
import QuoteService from "@src/services/payments/quotes"
import { window } from "browser-monads"
import { updateItinerary } from "./utils/updateItinerary"
const SummaryWrapper = styled.div`
  background-color: #8bbbaf;
  padding: 24px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`

const Title = styled.h2`
  color: ${props => props.theme.secondary900};
  font-size: 26px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: 0.5px;
  @media (max-width: 500px) {
    font-size: 24px;
  }
`

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1;
  @media (max-width: 730px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: center;
  color: ${props => props.theme.secondary800};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0.25px;
`

const Row = styled.div`
  display: flex;
  gap: 25px;
  margin-top: 11px;

  & span {
    color: #ffff;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    font-weight: bold;
  }
  @media (max-width: 730px) {
    flex-direction: column;
    gap: 8px;
    svg {
      transform: rotate(90deg);
    }
  }
`

const ButtonHolder = styled.div`
  max-width: 175px;
  margin-left: auto;
  & > button,
  & > a {
    display: flex;
    width: 100%;
    text-decoration: none;
    min-width: max-content;
    height: 50px;
    padding: 10px 18px;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: 500px) {
    max-width: 100%;
    width: 100%;
  }
`

const TripSummary = ({
  tour,
  nights,
  rooms,
  extras,
  lastDate,
  passengers,
  optionalExtras,
}) => {
  const [dates, setDates] = useState(["TBD", "TBD"])
  const [error, setError] = useState("")
  const {
    userFlowMode,
    showUserModal,
    setUserFlowMode,
    setShowUserModal,
    user,
  } = useContext(UserContext)
  const { watch, handleSubmit } = useFormContext()

  // Date Setting
  const startDate = watch("dates.start")
  const endDate = watch("dates.calculated_end")
  useEffect(() => {
    const formattedStart = startDate
      ? new Date(startDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : ""

    const formattedEnd = endDate
      ? new Date(endDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : ""
    setDates([formattedStart, formattedEnd])
  }, [startDate, endDate, setDates])

  // Tour Data
  const {
    databaseId,
    title,
    pricingTablesNew,
    itinerary,
    difficulty,
    tourOperator,
    tourCategory: { name: category },
  } = tour
  const formattedPricing = transformPricingTable(pricingTablesNew)
  // Main form submission flow
  const handleQuoteForm = async values => {
    const startDateRaw = values?.dates?.start
    const endDateRaw = values?.dates?.end
    // Validate: Traveller Name, Email
    if (!values.travellers.first_name || !values.travellers.last_name) {
      setError("Please enter your name before continuing.")
      return
    }
    if (!values.travellers.email) {
      setError("Please enter your email before continuing.")
      return
    }
    // Validate: Start date must be selected
    if (!startDateRaw) {
      setError("Please select a start date before continuing.")
      return
    }
    // Validate: End date can't exceed last date
    const calculatedEndDate = new Date(endDateRaw)
    if (lastDate && calculatedEndDate > lastDate) {
      setError(
        `The selected trip end date (${format(
          calculatedEndDate,
          "MMM d, yyyy",
        )}) exceeds the latest available tour date (${format(
          lastDate,
          "MMM d, yyyy",
        )}). Please pick an earlier start date or reduce trip length.`,
      )
      return
    }
    // Validate: At least one room must be selected
    const roomValues = values?.rooms || {}
    const totalRooms = Object.values(roomValues).reduce(
      (sum, val) => sum + Number(val || 0),
      0,
    )

    if (totalRooms === 0) {
      setError("Please select at least one room to continue.")
      return
    }

    setError("") // Clear any previous errors

    // Format start and end dates
    const startDate = format(new Date(startDateRaw), "yyyy-MM-dd")
    const endDate = format(calculatedEndDate, "yyyy-MM-dd")

    // Calculate total nights
    const nightValues = values?.nights || {}
    const totalNights = Object.values(nightValues).reduce(
      (sum, val) => sum + Number(val || 0),
      0,
    )
    // Map Extras
    const extras = optionalExtras.map(item => ({
      ...item,
      enabled: values.extras[item.key].enabled,
    }))

    const payload = {
      ...values,
      extras,
      dates: {
        generated: format(new Date(), "yyyy-MM-dd"),
        start: startDate,
        end: endDate,
      },
      nights: { ...values.nights, total: totalNights },
      rooms: { ...values.rooms, total: totalRooms },
      tour: {
        id: databaseId,
        operator: tourOperator.name,
        formattedPricing,
        inclusions: splitInclusions(
          tour.tourIncluded,
          tour.duration.nights,
          nights,
        ),
        title,
        difficulty,
        category,
        locationStart: itinerary.locationStart,
        locationEnd: itinerary.locationEnd,
        itinerary: updateItinerary(itinerary.days, values.nights, startDate),
        image: tour.images.feature,
      },
    }

    console.log(payload)

    const quoteNumber = Math.floor(100000 + Math.random() * 900000)
    payload.quote_number = quoteNumber
    if (user) {
      payload.travellers.profile_email = user.profile.email
    }
    try {
      const response = await QuoteService.createQuote(payload)
      if (response.success) {
        window.location.href = `/quote/${quoteNumber}`
      } else {
        console.error(response)
        setError("Quote creation failed.")
      }
    } catch (err) {
      console.error(err)
      setError("Error submitting quote.")
    }
  }

  return (
    <SummaryWrapper>
      <div>
        <Title>Trip summary</Title>
      </div>
      <InfoContainer>
        <InfoItem>
          Start location/date{" "}
          <Row>
            <span>
              {itinerary.locationStart} - {dates[0]}
            </span>
            <Arrow />
          </Row>
        </InfoItem>
        <InfoItem>
          End location/date{" "}
          <Row>
            <span>
              {itinerary.locationEnd} - {dates[1]}
            </span>
            <Arrow />
          </Row>
        </InfoItem>
        <InfoItem>
          Duration
          <Row>
            <span>{nights + 1} Days</span>
            <Arrow />
          </Row>
        </InfoItem>
        {/* <InfoItem>
          Distance
          <Row>
            <span>{tripData?.distance}</span>
            <Arrow />
          </Row>
        </InfoItem>*/}
        <InfoItem>
          Passengers
          <Row>
            <span>{passengers}</span>
            <Arrow />
          </Row>
        </InfoItem>
        <InfoItem>
          Rooms
          <Row>
            <span>{rooms}</span>
            <Arrow />
          </Row>
        </InfoItem>
        <InfoItem>
          Optional extras
          <Row>
            <span>{extras}</span>
          </Row>
        </InfoItem>
      </InfoContainer>
      <ButtonHolder>
        {user ? (
          <Button color="dark" onClick={handleSubmit(handleQuoteForm)}>
            See Pricing
          </Button>
        ) : (
          <Button
            color="dark"
            onClick={() => {
              setUserFlowMode("LOGIN_QUOTE")
              setShowUserModal(true)
            }}
          >
            See Pricing
          </Button>
        )}
      </ButtonHolder>
      {error && (
        <p style={{ color: "#a93226", marginTop: "12px", fontWeight: 500 }}>
          There was an issue with your submission:
          <br />- {error}
        </p>
      )}
      <UserActions
        defaultVisualMode={userFlowMode}
        $isOpen={showUserModal}
        setIsOpen={setShowUserModal}
        handlePricingClick={handleSubmit(handleQuoteForm)}
        error={error}
      />
    </SummaryWrapper>
  )
}

export default TripSummary

const Arrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="24"
      viewBox="0 0 26 24"
      fill="none"
    >
      <path
        d="M19.4602 12.0001C19.4551 11.474 19.2319 10.971 18.8389 10.6001L14.3219 6.30007C14.1246 6.11382 13.8578 6.00928 13.5796 6.00928C13.3014 6.00928 13.0346 6.11382 12.8373 6.30007C12.7386 6.39303 12.6603 6.50363 12.6068 6.62549C12.5534 6.74735 12.5258 6.87806 12.5258 7.01007C12.5258 7.14208 12.5534 7.27279 12.6068 7.39465C12.6603 7.5165 12.7386 7.62711 12.8373 7.72007L16.3014 11.0001H5.77216C5.49291 11.0001 5.22509 11.1054 5.02763 11.293C4.83017 11.4805 4.71924 11.7348 4.71924 12.0001C4.71924 12.2653 4.83017 12.5196 5.02763 12.7072C5.22509 12.8947 5.49291 13.0001 5.77216 13.0001H16.3014L12.8373 16.2901C12.639 16.477 12.5271 16.7312 12.5261 16.9965C12.5251 17.2619 12.6351 17.5168 12.832 17.7051C13.0289 17.8934 13.2965 17.9997 13.5759 18.0006C13.8553 18.0016 14.1236 17.897 14.3219 17.7101L18.8389 13.4101C19.2344 13.0367 19.4578 12.5296 19.4602 12.0001Z"
        fill="#2B4346"
      />
    </svg>
  )
}
